import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams } from 'react-router-dom'
import { masterclassService } from '../../services/masterclassService'
import { resolveImageUrl } from '../../utils/imageUrl'
import { SCHEDULE_SERIES_NAME, SCHEDULE_LABEL } from '../../utils/masterclassSchedule'

const BASE_URL = 'https://synergymultimediaacademy.com'

export default function MasterclassSchema() {
  const { slug } = useParams()
  const [masterclass, setMasterclass] = useState(null)

  useEffect(() => {
    if (!slug) return
    let mounted = true
    masterclassService.getMasterclassDetail(slug)
      .then((res) => { if (mounted) setMasterclass(res.masterclass) })
      .catch(() => {})
    return () => { mounted = false }
  }, [slug])

  if (!masterclass) return null

  const url = `${BASE_URL}/masterclass/${masterclass.slug || masterclass.id}`
  const image = resolveImageUrl(masterclass.banner_url) || `${BASE_URL}/Logos/SMA%20LOGO%20%20DARK%20MODE.png`
  const title = masterclass.meta_title || masterclass.topic || SCHEDULE_SERIES_NAME
  const description = masterclass.meta_description || masterclass.description || `Join industry experts at Synergy Multimedia Academy for the ${SCHEDULE_LABEL.toLowerCase()}.`
  const startDate = (() => {
    if (!masterclass.event_date) return null
    try {
      // Strip time component if event_date is a full ISO string
      const datePart = String(masterclass.event_date).split('T')[0]
      // Normalize start_time: keep only HH:MM if present
      let timePart = '17:00'
      if (masterclass.start_time) {
        const t = String(masterclass.start_time).trim()
        const match = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?/i)
        if (match) {
          let h = parseInt(match[1], 10)
          const m = match[2]
          const ampm = (match[3] || '').toUpperCase()
          if (ampm === 'PM' && h < 12) h += 12
          if (ampm === 'AM' && h === 12) h = 0
          timePart = `${String(h).padStart(2, '0')}:${m}`
        }
      }
      const d = new Date(`${datePart}T${timePart}`)
      return isNaN(d.getTime()) ? null : d.toISOString()
    } catch {
      return null
    }
  })()

  const eventSchema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: masterclass.topic,
    description: masterclass.description,
    startDate,
    endDate: startDate,
    eventStatus: masterclass.status === 'cancelled' ? 'https://schema.org/EventCancelled' : 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: masterclass.venue || 'Synergy Multimedia Academy, Kampala',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Kampala',
        addressCountry: 'UG',
      },
    },
    image,
    url,
    organizer: {
      '@type': 'Organization',
      name: 'Synergy Multimedia Academy',
      url: BASE_URL,
      logo: `${BASE_URL}/Logos/SMA%20LOGO%20%20DARK%20MODE.png`,
    },
    performer: masterclass.speaker_name
      ? {
          '@type': 'Person',
          name: masterclass.speaker_name,
          jobTitle: masterclass.speaker_title,
          worksFor: masterclass.speaker_company ? { '@type': 'Organization', name: masterclass.speaker_company } : undefined,
        }
      : undefined,
    offers: {
      '@type': 'Offer',
      price: '150000',
      priceCurrency: 'UGX',
      availability: masterclass.registration_open ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
      url,
      validFrom: masterclass.registration_deadline || masterclass.event_date,
      description: 'UGX 150,000 for non-SMA guests. FREE for current SMA students.',
    },
  }

  if (!eventSchema.performer) delete eventSchema.performer

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      <meta property="og:type" content="event" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Synergy Multimedia Academy" />
      <meta property="event:start_time" content={startDate} />
      <meta property="event:end_time" content={startDate} />
      <meta property="event:location" content={masterclass.venue || 'Synergy Multimedia Academy, Kampala'} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
    </Helmet>
  )
}
